import { useCallback, useReducer, useRef } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { CustomFieldMappingKey } from '../../common/types';
import type { FieldRestorationState, FieldRestorationActions } from './types';
import { reducer } from './utils';

export const useRestoreFields = (
	...mappingKeys: CustomFieldMappingKey[]
): [FieldRestorationState, FieldRestorationActions] => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [state, dispatch] = useReducer(reducer, {
		loading: false,
		success: null,
		error: null,
		hasRun: false,
	});

	const mappingKeysRef = useRef(mappingKeys);
	mappingKeysRef.current = mappingKeys;
	const restoreFields = useCallback(
		(actionSubject: string) => {
			dispatch({ type: 'LOADING' });

			const url = '/rest/servicedesk/1/servicedesk/fields/restore';

			return performPostRequest(url, {
				body: JSON.stringify({ mappingKeys: mappingKeysRef.current }),
			})
				.then(() => {
					dispatch({ type: 'SUCCESS' });
					fireTrackAnalytics(createAnalyticsEvent({}), `${actionSubject} restored`);
				})
				.catch((error) => {
					dispatch({ type: 'ERROR', error });
					if (!isClientFetchError(error)) {
						fireErrorAnalytics({
							error,
							meta: {
								id: `useRestoreFields.restoreFields.${actionSubject}`,
								packageName: 'jiraServicedeskCustomFields',
								teamName: 'jenga',
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}
					throw error;
				});
		},
		[createAnalyticsEvent],
	);

	return [state, { restoreFields }];
};
